import { useState } from "react";
import { Box, Container, Typography, Paper, Tabs, Tab, Divider } from "@mui/material";
import PendingApprovalsCard from "./MyWallet/panels/PendingApprovalsCard";
import ReconciliationDashboard from "./MyWallet/panels/ReconciliationDashboard";

/**
 * Demo page to review the new Pending Approvals UI
 *
 * Access: Navigate to /wallet/approvals-demo
 */
const PendingApprovalsDemo = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, mb: 4, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h3" gutterBottom>
          Pending Approvals - New UI Design
        </Typography>
        <Typography variant="body1">
          This page demonstrates the new 5-section approval pipeline with reconciliation tracking.
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 2, opacity: 0.9 }}>
          📊 Using mock data from <code>mockWalletData.js</code>
        </Typography>
      </Paper>

      {/* Tabs for switching views */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} variant="fullWidth">
          <Tab label="Approval Pipeline Overview" />
          <Tab label="Reconciliation Dashboard" />
        </Tabs>
      </Paper>

      {/* Tab 0: Approval Pipeline */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <PendingApprovalsCard />
          </Box>

          <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              About This Design
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>5 Approval Types:</strong>
            </Typography>
            <ul style={{ marginTop: 0, paddingLeft: 20 }}>
              <li><Typography variant="body2">💰 Customer Top-ups - Money IN (awaiting verification)</Typography></li>
              <li><Typography variant="body2">🏢 Partner Payments - Money OUT (awaiting approval)</Typography></li>
              <li><Typography variant="body2">💳 Approved Payments - Money OUT (awaiting bank transfer)</Typography></li>
              <li><Typography variant="body2">⏳ In Transit - Money OUT (awaiting partner confirmation) <strong>⚠️ NEW</strong></Typography></li>
              <li><Typography variant="body2">🤝 Channel Commissions - Money OUT (monthly payouts)</Typography></li>
            </ul>
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              <strong>Key Features:</strong>
            </Typography>
            <ul style={{ marginTop: 0, paddingLeft: 20 }}>
              <li><Typography variant="body2">Clear money location tracking (In bank vs Left bank)</Typography></li>
              <li><Typography variant="body2">Priority-based color coding</Typography></li>
              <li><Typography variant="body2">Reconciliation status (Normal, Follow-up, Overdue)</Typography></li>
              <li><Typography variant="body2">Urgent action alerts</Typography></li>
              <li><Typography variant="body2">Direct navigation to detailed views</Typography></li>
            </ul>
          </Paper>
        </Box>
      )}

      {/* Tab 1: Reconciliation Dashboard */}
      {activeTab === 1 && (
        <Box>
          <ReconciliationDashboard />

          <Paper sx={{ p: 3, bgcolor: 'grey.50', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              About Reconciliation Dashboard
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Purpose:</strong> Track payments AFTER they leave Thaklis bank and before partner confirms receipt.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>3 Reconciliation States:</strong>
            </Typography>
            <ul style={{ marginTop: 0, paddingLeft: 20 }}>
              <li><Typography variant="body2">🟢 <strong>Normal (0-2 days):</strong> Within expected timeline - no action needed</Typography></li>
              <li><Typography variant="body2">🟡 <strong>Follow-up (3-5 days):</strong> Should send reminder to partner</Typography></li>
              <li><Typography variant="body2">🔴 <strong>Overdue (>5 days):</strong> Investigation required - possible dispute</Typography></li>
            </ul>
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              <strong>Features:</strong>
            </Typography>
            <ul style={{ marginTop: 0, paddingLeft: 20 }}>
              <li><Typography variant="body2">Expandable rows with complete payment history</Typography></li>
              <li><Typography variant="body2">Follow-up contact history tracking</Typography></li>
              <li><Typography variant="body2">Investigation workflow for overdue payments</Typography></li>
              <li><Typography variant="body2">Auto-confirm date tracking</Typography></li>
              <li><Typography variant="body2">Quick action buttons (Contact, View Statement, Confirm)</Typography></li>
            </ul>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default PendingApprovalsDemo;
